import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './se-contact-teaser.css.lit';
import '../../base/outline-icon/outline-icon';
import '../se-link/se-link';
import '../se-heading/se-heading';
import { AllowedHeadingLevels } from '../se-heading/config';
import { SlotController } from '../../controllers/slot-controller';

/**
 * The SeContactTeaser component
 * @element se-contact-teaser
 * @slot contact-image
 * @slot bonus-link
 */
@customElement('se-contact-teaser')
export class ContactTeaser extends OutlineElement {
  slots = new SlotController(this, true);
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String })
  name: string;
  @property({ type: String, attribute: 'heading-level' })
  headingLevel: AllowedHeadingLevels = 'h3';
  @property({ type: String, attribute: 'professional-title' })
  professionalTitle: string;
  @property({ type: String, attribute: 'phone-number' })
  phoneNumber: string;
  @property({ type: String })
  email: string;
  @property({ type: String, attribute: 'bio-link' })
  bioLink: string;

  @state() hasBonusLink: boolean;

  firstUpdated(): void {
    this.hasBonusLink = this.slots.test('bonus-link');
  }

  bonusLink(): TemplateResult {
    if (this.hasBonusLink) {
      return html`<span class="se-contact-teaser__bonus-link">
        <slot name="bonus-link"></slot>
      </span>`;
    } else {
      return html``;
    }
  }

  contactImage(): TemplateResult {
    if (this.querySelector('[slot="contact-image"]')) {
      return html`<div class="se-contact-teaser__image">
        <slot name="contact-image"></slot>
      </div>`;
    } else {
      return html``;
    }
  }

  contactContent(): TemplateResult {
    return html`
      <div class="se-contact-teaser__content">
        <se-heading
          level="${this.headingLevel}"
          level-size="2xl"
          level-style="normal"
          class="se-contact-teaser__name"
          >${this.name}</se-heading
        >
        ${this.professionalTitle
          ? html`<p class="se-contact-teaser__title">
              ${this.professionalTitle}
            </p>`
          : ''}
        ${this.phoneNumber || this.email || this.hasBonusLink
          ? html`
              <span class="se-contact-teaser__contact-links">
                ${this.phoneNumber
                  ? html` <se-link
                      class="se-contact-teaser__phone"
                      link-href="tel:${this.phoneNumber}"
                      >${this.phoneNumber}</se-link
                    >`
                  : ''}
                ${this.phoneNumber && this.email
                  ? html`<span class="separator">|</span>`
                  : ''}
                ${this.email
                  ? html`<span class="se-contact-teaser__email">
                      <se-link link-href="mailto:${this.email}">
                        <outline-icon
                          name="mail"
                          library="ionicons"
                          color="brand-primary-2"
                          size="1.2rem"
                          label="Send an email"
                        />
                      </se-link>
                    </span>`
                  : ''}
                ${(this.phoneNumber || this.email) && this.hasBonusLink
                  ? html`<span class="separator">|</span>`
                  : ''}
                ${this.bonusLink()}
              </span>
            `
          : ''}
        ${this.bioLink
          ? html`<se-link
              class="se-contact-teaser__bio"
              link-href="${this.bioLink}"
              >View bio</se-link
            >`
          : ''}
      </div>
    `;
  }

  render(): TemplateResult {
    return html`
      <div class="se-contact-teaser">
        ${this.contactImage()} ${this.contactContent()}
      </div>
    `;
  }
}
